import React, { useState, useRef, useMemo } from "react";
import Page from "../components/Page";
import {
  IconBuildingWarehouse,
  IconArrowRight,
  IconPlus,
  IconCheck,
  IconX,
  IconPrinter,
  IconTrash,
  IconClock,
  IconInfoCircle,
  IconSearch,
  IconFileText,
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import {
  useBranches,
  useStockTransfers,
  useBranchInventory,
  createStockTransfer,
  receiveStockTransfer,
  cancelStockTransfer,
} from "../controllers/branch.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import { getUserDetailsInLocalStorage } from "../helpers/UserDetails";
import { toast } from "react-hot-toast";
import { useTheme } from "../contexts/ThemeContext";
import moment from "moment";
import clsx from "clsx";

export default function StockTransfersPage() {
  const { branches } = useBranches();
  const { transfers, isLoading, error, mutate } = useStockTransfers();
  const { data: storeSettings } = useStoreSettings();
  const currentUser = getUserDetailsInLocalStorage() || {};
  const { theme } = useTheme();

  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'PENDING' | 'COMPLETED' | 'CANCELLED'
  const [searchTerm, setSearchTerm] = useState("");

  // Modal State
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [selectedTransferForReceive, setSelectedTransferForReceive] = useState(null);

  // New Transfer Form State
  const activeBranchId = localStorage.getItem("active_branch_id");
  const [fromBranchId, setFromBranchId] = useState(activeBranchId || "");
  const [toBranchId, setToBranchId] = useState("");
  const [transferNotes, setTransferNotes] = useState("");
  const [transferItems, setTransferItems] = useState([
    { inventory_item_id: "", quantity: "", unit: "", title: "", current_stock: 0 },
  ]);

  // Inventory of the source branch to populate item selector and show stock
  const { data: sourceInventory } = useBranchInventory(fromBranchId || null);

  // Available destination branches (exclude source branch)
  const availableToBranches = useMemo(() => {
    if (!branches) return [];
    return branches.filter((b) => Number(b.id) !== Number(fromBranchId) && b.is_active);
  }, [branches, fromBranchId]);

  // Filter transfers
  const filteredTransfers = useMemo(() => {
    if (!transfers) return [];
    return transfers.filter((t) => {
      if (activeTab !== "all" && t.status !== activeTab) {
        return false;
      }
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const numMatch = t.transfer_no?.toLowerCase().includes(q);
        const fromMatch = t.from_branch_name?.toLowerCase().includes(q);
        const toMatch = t.to_branch_name?.toLowerCase().includes(q);
        const noteMatch = t.notes?.toLowerCase().includes(q);
        return numMatch || fromMatch || toMatch || noteMatch;
      }
      return true;
    });
  }, [transfers, activeTab, searchTerm]);

  // Handlers for dynamic items in New Transfer
  const handleAddItemRow = () => {
    setTransferItems((prev) => [
      ...prev,
      { inventory_item_id: "", quantity: "", unit: "", title: "", current_stock: 0 },
    ]);
  };

  const handleRemoveItemRow = (index) => {
    if (transferItems.length === 1) {
      toast.error("At least one item is required.");
      return;
    }
    setTransferItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, itemId) => {
    const selected = (sourceInventory || []).find((item) => Number(item.id) === Number(itemId));
    setTransferItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        inventory_item_id: itemId,
        title: selected?.title || "",
        unit: selected?.unit || "pc",
        current_stock: selected?.branch_quantity !== undefined ? parseFloat(selected.branch_quantity) : parseFloat(selected?.quantity || 0),
      };
      return next;
    });
  };

  const handleQuantityChange = (index, qty) => {
    setTransferItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], quantity: qty };
      return next;
    });
  };

  const handleOpenNewTransfer = () => {
    const defaultFrom = activeBranchId || (branches && branches[0]?.id) || "";
    setFromBranchId(defaultFrom);
    const firstOther = (branches || []).find((b) => Number(b.id) !== Number(defaultFrom) && b.is_active);
    setToBranchId(firstOther?.id || "");
    setTransferNotes("");
    setTransferItems([{ inventory_item_id: "", quantity: "", unit: "", title: "", current_stock: 0 }]);
    setIsNewModalOpen(true);
  };

  const handleSubmitTransfer = async (e) => {
    e.preventDefault();
    if (!fromBranchId) {
      toast.error("Please select the source branch.");
      return;
    }
    if (!toBranchId) {
      toast.error("Please select the destination branch.");
      return;
    }
    if (Number(fromBranchId) === Number(toBranchId)) {
      toast.error("Source and destination branch cannot be the same.");
      return;
    }

    // Validate items
    for (let i = 0; i < transferItems.length; i++) {
      const item = transferItems[i];
      if (!item.inventory_item_id) {
        toast.error(`Please select an item on row ${i + 1}`);
        return;
      }
      const qty = parseFloat(item.quantity);
      if (isNaN(qty) || qty <= 0) {
        toast.error(`Invalid quantity for item "${item.title || `row ${i + 1}`}"`);
        return;
      }
      if (qty > item.current_stock) {
        toast.error(`Quantity for "${item.title}" (${qty}) exceeds available stock at source branch (${item.current_stock})`);
        return;
      }
    }

    try {
      toast.loading("Dispatching stock transfer...");
      await createStockTransfer({
        from_branch_id: fromBranchId,
        to_branch_id: toBranchId,
        notes: transferNotes,
        items: transferItems.map((it) => ({
          inventory_item_id: it.inventory_item_id,
          quantity: parseFloat(it.quantity),
          unit: it.unit,
        })),
      });
      await mutate();
      toast.dismiss();
      toast.success("Stock transfer dispatched! Stock deducted at source branch.");
      setIsNewModalOpen(false);
    } catch (err) {
      toast.dismiss();
      const msg = err?.response?.data?.message || err?.message || "Failed to dispatch transfer";
      toast.error(msg);
    }
  };

  const handleOpenReceive = (transfer) => {
    setSelectedTransferForReceive(transfer);
    setIsReceiveModalOpen(true);
  };

  const handleConfirmReceive = async () => {
    if (!selectedTransferForReceive) return;
    try {
      toast.loading("Confirming stock receipt...");
      await receiveStockTransfer(selectedTransferForReceive.id);
      await mutate();
      toast.dismiss();
      toast.success("Stock received successfully and added to destination branch inventory!");
      setIsReceiveModalOpen(false);
      setSelectedTransferForReceive(null);
    } catch (err) {
      toast.dismiss();
      const msg = err?.response?.data?.message || err?.message || "Failed to receive transfer";
      toast.error(msg);
    }
  };

  const handleCancelTransfer = async (transfer) => {
    if (!window.confirm(`Are you sure you want to cancel transfer ${transfer.transfer_no}? Dispatched stock will be refunded back to ${transfer.from_branch_name}.`)) {
      return;
    }
    try {
      toast.loading("Cancelling transfer...");
      await cancelStockTransfer(transfer.id);
      await mutate();
      toast.dismiss();
      toast.success("Transfer cancelled. Stock restored to origin branch.");
    } catch (err) {
      toast.dismiss();
      const msg = err?.response?.data?.message || err?.message || "Failed to cancel transfer";
      toast.error(msg);
    }
  };

  const handlePrintTransferPass = (t) => {
    const storeName = storeSettings?.store_name || storeSettings?.name || "Stock Transfer Gate Pass";
    const storePhone = storeSettings?.phone || "";
    const storePan = storeSettings?.pan_no || "";

    const itemsHtml = (t.items || []).map((it, idx) => `
      <tr style="border-bottom: 1px solid #e5e7eb;">
        <td style="padding: 8px 10px; font-size: 11px; text-align: center;">${idx + 1}</td>
        <td style="padding: 8px 10px; font-size: 11px; font-weight: 600;">${it.item_title || "Item #" + it.inventory_item_id}</td>
        <td style="padding: 8px 10px; font-size: 11px; text-align: right; font-weight: bold;">${it.dispatched_quantity} ${it.unit || ""}</td>
      </tr>
    `).join("");

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Stock Transfer Pass - ${t.transfer_no}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 25px; color: #111827; }
          .header { text-align: center; border-bottom: 2px solid #059669; padding-bottom: 12px; margin-bottom: 16px; }
          .title { font-size: 18px; font-weight: bold; text-transform: uppercase; color: #059669; }
          .meta-grid { display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 12px; }
          .meta-box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 14px; width: 48%; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background: #f3f4f6; font-size: 11px; text-transform: uppercase; padding: 8px 10px; border-bottom: 1px solid #d1d5db; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; font-size: 12px; }
          .sign-line { border-top: 1px dashed #9ca3af; width: 180px; text-align: center; padding-top: 6px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div style="font-size: 20px; font-weight: bold;">${storeName}</div>
          <div class="title" style="margin-top: 4px;">Inter-Branch Stock Transfer Voucher</div>
          <div style="font-size: 11px; color: #6b7280; margin-top: 2px;">Transfer No: <strong>${t.transfer_no}</strong> • Date: ${moment(t.dispatched_at || t.created_at).format("DD/MM/YYYY, h:mm A")}</div>
        </div>

        <div class="meta-grid">
          <div class="meta-box">
            <div style="font-weight: bold; font-size: 11px; text-transform: uppercase; color: #6b7280; margin-bottom: 4px;">From Origin Branch:</div>
            <div style="font-size: 13px; font-weight: bold; color: #111827;">${t.from_branch_name} (${t.from_branch_code})</div>
            <div style="font-size: 11px; color: #4b5563; margin-top: 2px;">Dispatched by: ${t.created_by || "Staff"}</div>
          </div>
          <div class="meta-box">
            <div style="font-weight: bold; font-size: 11px; text-transform: uppercase; color: #6b7280; margin-bottom: 4px;">To Destination Branch:</div>
            <div style="font-size: 13px; font-weight: bold; color: #059669;">${t.to_branch_name} (${t.to_branch_code})</div>
            <div style="font-size: 11px; color: #4b5563; margin-top: 2px;">Status: <strong>${t.status}</strong></div>
          </div>
        </div>

        ${t.notes ? `<div style="font-size: 11px; background: #f9fafb; padding: 8px 12px; border-radius: 6px; margin-bottom: 14px; border: 1px solid #e5e7eb;"><strong>Transfer Memo:</strong> ${t.notes}</div>` : ""}

        <table>
          <thead>
            <tr>
              <th style="width: 35px; text-align: center;">#</th>
              <th style="text-align: left;">Item Description</th>
              <th style="text-align: right; width: 120px;">Dispatched Qty</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>

        <div class="signatures">
          <div class="sign-line">Dispatched By (Sender)</div>
          <div class="sign-line">Received By (Receiver)</div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=800,height=650");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 300);
    }
  };

  return (
    <Page>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-gray-100 dark:border-neutral-800">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
            <IconBuildingWarehouse size={28} stroke={iconStroke} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              Inter-Branch Stock Transfers
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Transfer raw materials & items between branches with live inventory stock synchronization.
            </p>
          </div>
        </div>

        <button
          onClick={handleOpenNewTransfer}
          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl font-semibold text-sm bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition active:scale-95 cursor-pointer w-fit"
        >
          <IconPlus size={18} stroke={iconStroke} />
          New Stock Transfer
        </button>
      </div>

      {/* Tabs & Search */}
      <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-gray-100 dark:bg-neutral-800 w-fit">
          {[
            { key: "all", label: "All Transfers" },
            { key: "PENDING", label: "In Transit (Pending)" },
            { key: "COMPLETED", label: "Completed" },
            { key: "CANCELLED", label: "Cancelled" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={clsx(
                "px-3.5 py-1.5 rounded-xl text-xs font-semibold transition",
                activeTab === tab.key
                  ? "bg-white dark:bg-neutral-900 text-emerald-600 dark:text-emerald-400 shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-64">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search transfer #, branch, memo..."
            className="w-full text-xs rounded-xl pl-9 pr-3.5 py-2 border border-restro-border-green bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="mt-5 rounded-2xl border border-restro-border-green bg-white dark:bg-neutral-900 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-50 dark:bg-neutral-800/60 text-gray-500 dark:text-gray-400 font-semibold border-b border-gray-100 dark:border-neutral-800">
              <tr>
                <th className="py-3 px-4">Transfer #</th>
                <th className="py-3 px-4">Date & Time</th>
                <th className="py-3 px-4">Origin & Destination</th>
                <th className="py-3 px-4">Items Manifest</th>
                <th className="py-3 px-4">Dispatched By</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-400">
                    Loading transfer history...
                  </td>
                </tr>
              ) : filteredTransfers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <IconBuildingWarehouse size={36} className="mx-auto mb-2 opacity-30" />
                    No stock transfers found. Click "New Stock Transfer" to create one.
                  </td>
                </tr>
              ) : (
                filteredTransfers.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50/50 dark:hover:bg-neutral-800/40 transition">
                    <td className="py-3.5 px-4 font-mono font-semibold text-gray-900 dark:text-gray-100">
                      {t.transfer_no}
                    </td>

                    <td className="py-3.5 px-4 text-gray-500 dark:text-gray-400">
                      <div>{moment(t.dispatched_at || t.created_at).format("DD MMM YYYY")}</div>
                      <div className="text-[10px] text-gray-400">{moment(t.dispatched_at || t.created_at).format("hh:mm A")}</div>
                    </td>

                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                          {t.from_branch_name}
                        </span>
                        <IconArrowRight size={14} className="text-emerald-500 shrink-0" />
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                          {t.to_branch_name}
                        </span>
                      </div>
                      {t.notes && (
                        <div className="text-[11px] text-gray-400 truncate max-w-xs mt-0.5">
                          Memo: {t.notes}
                        </div>
                      )}
                    </td>

                    <td className="py-3.5 px-4">
                      <div className="space-y-1">
                        {(t.items || []).slice(0, 3).map((it, idx) => (
                          <div key={idx} className="text-[11px] text-gray-600 dark:text-gray-300">
                            • <strong className="text-gray-900 dark:text-gray-100">{it.item_title || `Item #${it.inventory_item_id}`}</strong>: {it.dispatched_quantity} {it.unit}
                          </div>
                        ))}
                        {(t.items || []).length > 3 && (
                          <div className="text-[10px] text-gray-400 italic">
                            + {(t.items || []).length - 3} more item(s)
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="py-3.5 px-4 text-gray-600 dark:text-gray-300">
                      {t.created_by || "Admin"}
                    </td>

                    <td className="py-3.5 px-4 text-center">
                      <span
                        className={clsx(
                          "px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider",
                          t.status === "PENDING" && "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20",
                          t.status === "COMPLETED" && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20",
                          t.status === "CANCELLED" && "bg-red-500/10 text-red-500 border border-red-500/20"
                        )}
                      >
                        {t.status === "PENDING" ? "In Transit" : t.status}
                      </span>
                    </td>

                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handlePrintTransferPass(t)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-neutral-800 transition"
                          title="Print Gate Pass / Voucher"
                        >
                          <IconPrinter size={16} stroke={iconStroke} />
                        </button>

                        {t.status === "PENDING" && (
                          <>
                            <button
                              onClick={() => handleOpenReceive(t)}
                              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500 hover:text-white transition active:scale-95"
                              title="Confirm Arrival & Add Stock at Destination"
                            >
                              Receive
                            </button>
                            <button
                              onClick={() => handleCancelTransfer(t)}
                              className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-neutral-800 transition"
                              title="Cancel & Revert Stock"
                            >
                              <IconX size={16} stroke={iconStroke} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: New Stock Transfer */}
      {isNewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-900 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl border border-restro-border-green">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-neutral-800">
              <div className="flex items-center gap-2.5">
                <IconBuildingWarehouse size={22} className="text-emerald-500" />
                <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">
                  New Inter-Branch Stock Transfer
                </h3>
              </div>
              <button
                onClick={() => setIsNewModalOpen(false)}
                className="p-1.5 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-neutral-800"
              >
                <IconX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitTransfer} className="mt-5 space-y-4">
              {/* Branch Selectors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    From Origin Branch (Deduct Stock) *
                  </label>
                  <select
                    value={fromBranchId}
                    onChange={(e) => setFromBranchId(e.target.value)}
                    required
                    className="w-full text-xs rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium"
                  >
                    <option value="">Select Origin Branch</option>
                    {(branches || []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branch_name} ({b.branch_code}){b.is_main ? " - Main" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    To Destination Branch (Receive Stock) *
                  </label>
                  <select
                    value={toBranchId}
                    onChange={(e) => setToBranchId(e.target.value)}
                    required
                    className="w-full text-xs rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium"
                  >
                    <option value="">Select Destination Branch</option>
                    {availableToBranches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branch_name} ({b.branch_code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Memo / Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Transfer Memo / Remarks
                </label>
                <input
                  type="text"
                  value={transferNotes}
                  onChange={(e) => setTransferNotes(e.target.value)}
                  placeholder="e.g., Weekly kitchen ingredients transfer, driver #1"
                  className="w-full text-xs rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>

              {/* Items Manifest */}
              <div className="pt-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Items to Transfer
                  </label>
                  <button
                    type="button"
                    onClick={handleAddItemRow}
                    className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700"
                  >
                    <IconPlus size={14} /> Add Item
                  </button>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {transferItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-100 dark:border-neutral-800 bg-gray-50/60 dark:bg-neutral-800/40"
                    >
                      <div className="flex-1">
                        <select
                          value={item.inventory_item_id}
                          onChange={(e) => handleItemChange(index, e.target.value)}
                          required
                          className="w-full text-xs rounded-lg px-2.5 py-2 border border-restro-border-green bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium"
                        >
                          <option value="">Select Item...</option>
                          {(sourceInventory || []).map((inv) => {
                            const bQty = inv.branch_quantity !== undefined ? parseFloat(inv.branch_quantity) : parseFloat(inv.quantity || 0);
                            return (
                              <option key={inv.id} value={inv.id}>
                                {inv.title} (Available: {bQty} {inv.unit})
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="w-28 flex items-center gap-1">
                        <input
                          type="number"
                          step="any"
                          min="0.001"
                          max={item.current_stock || 999999}
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(index, e.target.value)}
                          placeholder="Qty"
                          required
                          className="w-full text-xs rounded-lg px-2 py-2 border border-restro-border-green bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-emerald-500 text-right font-mono"
                        />
                        <span className="text-xs text-gray-400 w-8 truncate">{item.unit || "unit"}</span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveItemRow(index)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 transition"
                        title="Remove row"
                      >
                        <IconTrash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modal Actions */}
              <div className="pt-4 border-t border-gray-100 dark:border-neutral-800 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsNewModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium border border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm flex items-center gap-1.5"
                >
                  <IconCheck size={16} /> Dispatch Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Confirm Stock Receipt */}
      {isReceiveModalOpen && selectedTransferForReceive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-900 rounded-3xl w-full max-w-md p-6 shadow-2xl border border-restro-border-green">
            <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <IconCheck size={22} className="text-emerald-500" />
              Confirm Stock Receipt
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Confirm that items from transfer <strong>{selectedTransferForReceive.transfer_no}</strong> arrived at <strong>{selectedTransferForReceive.to_branch_name}</strong>.
            </p>

            <div className="mt-4 p-3 rounded-xl bg-gray-50 dark:bg-neutral-800/60 border border-gray-100 dark:border-neutral-800 space-y-1.5 text-xs">
              <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Items to be added to inventory:
              </div>
              {(selectedTransferForReceive.items || []).map((it, idx) => (
                <div key={idx} className="flex justify-between text-gray-600 dark:text-gray-300">
                  <span>{it.item_title || `Item #${it.inventory_item_id}`}</span>
                  <strong className="text-emerald-600 dark:text-emerald-400">
                    +{it.dispatched_quantity} {it.unit}
                  </strong>
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsReceiveModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-medium border border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReceive}
                className="px-5 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
              >
                Confirm & Add to Stock
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
